import { useEffect, type ReactNode } from 'react'
import { motion, useDragControls } from 'motion/react'
import { X } from 'lucide-react'

/**
 * Hoja inferior: lo que se elige o se escribe, al alcance del pulgar.
 *
 * Sube con un rebote suave y se cierra arrastrándola hacia abajo, tocando
 * fuera o con Escape. Para que anime también al cerrarse, quien la abre la
 * envuelve en `<AnimatePresence>`.
 */
export function Hoja({
  titulo,
  descripcion,
  onCerrar,
  children,
}: {
  titulo: string
  descripcion?: string
  onCerrar: () => void
  children: ReactNode
}) {
  const drag = useDragControls()

  useEffect(() => {
    const onEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCerrar()
    }
    document.addEventListener('keydown', onEscape)
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onEscape)
      document.body.style.overflow = overflow
    }
  }, [onCerrar])

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" role="presentation">
      <motion.div
        className="absolute inset-0 bg-black/35 backdrop-blur-[2px] dark:bg-black/55"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={onCerrar}
      />
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        className="relative flex max-h-[88dvh] w-full max-w-2xl flex-col rounded-t-3xl border-t border-border bg-card shadow-[0_-12px_40px_-20px_rgb(0_0_0/0.25)] dark:border-white/10"
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', stiffness: 380, damping: 32 }}
        drag="y"
        dragConstraints={{ top: 0, bottom: 0 }}
        dragElastic={{ top: 0, bottom: 0.6 }}
        dragControls={drag}
        dragListener={false}
        dragSnapToOrigin
        onDragEnd={(_, info) => {
          if (info.offset.y > 120 || info.velocity.y > 600) onCerrar()
        }}
      >
        {/* El tirador: sólo desde aquí se arrastra, para no pelearse con el scroll. */}
        <div
          className="flex h-6 w-full shrink-0 cursor-grab touch-none items-center justify-center active:cursor-grabbing"
          onPointerDown={(event) => drag.start(event)}
          aria-hidden="true"
        >
          <span className="h-1.5 w-10 rounded-full bg-border" />
        </div>
        <header className="flex items-start justify-between gap-3 px-5 pt-1">
          <div className="min-w-0">
            <h2 className="font-display text-2xl leading-tight">{titulo}</h2>
            {descripcion && <p className="mt-1 text-sm text-muted-foreground">{descripcion}</p>}
          </div>
          <button
            type="button"
            aria-label="Cerrar"
            onClick={onCerrar}
            className="-mr-2 grid h-10 w-10 shrink-0 place-items-center rounded-full text-muted-foreground outline-none transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-5 w-5" />
          </button>
        </header>
        <div className="mt-3 overflow-y-auto overscroll-contain px-5 pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
          {children}
        </div>
      </motion.div>
    </div>
  )
}

